import { AreaEnum } from '@/queries/useEnums';
import {
  BuildingAlternative,
  TechnologyParameter,
} from '@/stores/SubprojectStore';
import { getUnitLimit } from '../queries/techMetrics';
import {
  EnergyKind,
  HVAC,
  HVACMinMax,
  Tab,
  TechSortState,
  sortTechItemsByState,
} from '../types';
import { normalizeToRatingLimit } from '../utils';

/**
 * For a given building calculate the total possible HVAC units necessary.
 *
 * Converts the building's conditioned area to square feet using the server's
 * area-unit enum, then applies the shared limit rule in `getUnitLimit`, so the
 * result is never less than 1.
 */
export function getPossibleUnitsForHvac(
  hvac: HVAC,
  hvacUnit: AreaEnum,
  building: BuildingAlternative,
  buildingUnit: AreaEnum,
): number {
  const conditionedArea: number =
    building.buildingParams.conditionedSpace.value;

  // building unit -> m^2 -> the unit the catalog factor is expressed in (sqft)
  const conditionedAreaInHvacUnit: number =
    (conditionedArea / buildingUnit.unitPerM2) * hvacUnit.unitPerM2;

  // The API serializes this DecimalField as a string despite the HVAC type;
  // convert explicitly, as addMetricsToResource does.
  return getUnitLimit(
    Number(hvac.unitsNeededPerSqft),
    conditionedAreaInHvacUnit,
  );
}

/**
 * Calculate the energy usage for an HVAC unit against the air exchange multiplier
 */
export function getEnergyUsageForHvac(
  hvac: HVAC,
  airExchange: number,
  units: number,
): number {
  let energy = hvac.energyUsePerYr * units;
  if (hvac.airFactorMultiplier > 0) {
    energy *= hvac.airFactorMultiplier * airExchange;
  }
  return energy;
}

/**
 * Whether an HVAC system may be used on a given alternative tab.
 *
 * Conventional allows everything; Low Carbon excludes gas; Net Zero excludes
 * gas and any pure-electric system rated above 2 energy icons. The rating is
 * computed on the scaled value (energyUsePerYr × unitsNeededPerSqft) so it
 * matches the min/max bounds behind the card icons.
 *
 * Single source of truth for the tab rules — used both when listing available
 * systems and when copying a selection between tabs.
 */
export function isHvacAllowedOnTab(
  hvac: HVAC,
  tab: Tab,
  minMax: HVACMinMax | undefined,
): boolean {
  if (tab === 'reducedCarbonAlternative') {
    return !hvac.energySink.includes('Gas');
  }

  if (tab === 'zeroCarbonAlternative') {
    if (hvac.energySink.includes('Gas')) {
      return false;
    }
    if (hvac.energySink === 'Electricity') {
      const normalizedEnergy = normalizeToRatingLimit(
        minMax?.energyUsePerYr?.min ?? 0,
        minMax?.energyUsePerYr?.max ?? 0,
        hvac.energyUsePerYr,
        hvac.unitsNeededPerSqft,
      );
      if (normalizedEnergy > 2) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Compute the list of HVAC systems available for selection on a tab.
 *
 * Applies, in order: alternative-type restrictions (Low Carbon: no gas;
 * Net Zero: no gas and only low-energy electric systems), exclusion of
 * already-selected systems, the energy-type filter, the search query, and
 * finally the active sort. Runs the same regardless of whether anything is
 * selected yet — an empty selection must not bypass the tab restrictions.
 */
export function getAvailableHvacList(
  hvacs: HVAC[],
  selectedParams: TechnologyParameter[],
  tab: Tab,
  searchString: string,
  energyFilter: EnergyKind[],
  sortState: TechSortState,
  minMax: HVACMinMax | undefined,
): HVAC[] {
  const filtered = hvacs.filter((hvacSystem) => {
    if (!isHvacAllowedOnTab(hvacSystem, tab, minMax)) {
      return false;
    }

    // don't show already selected hvacs
    const notInSelectedList = !selectedParams.some(
      (selectedHvacSystem) => selectedHvacSystem.techId === hvacSystem.id,
    );

    const passesFilter =
      energyFilter.length === 0 || energyFilter.includes(hvacSystem.energySink);

    // include only hvacs with names that include searchString
    const partOfQuery =
      searchString.length === 0 ||
      hvacSystem.name.toLowerCase().includes(searchString.toLowerCase());

    return notInSelectedList && partOfQuery && passesFilter;
  });

  return sortTechItemsByState(filtered, sortState);
}

export interface HvacCategory {
  /** Stored `api_hvac.hvac_type` value, trimmed and lowercased. */
  type: string;
  /** Heading shown above the category's cards. */
  label: string;
  /**
   * Share of the HVAC system coverage (header pie) a card in this category
   * contributes at its limit. Heating 30%, cooling 30%, air handling and
   * ventilation 40%; efficiency adds nothing (settled with Jan Whittington).
   */
  coverageWeight: number;
}

/**
 * The known HVAC card categories (`api_hvac.hvac_type` values), in display
 * order, with their headings.
 *
 * H-V-AC acronym order — heating, ventilation, air conditioning (cooling) —
 * which is also the order the model's help_text lists them, then efficiency.
 * A type that appears in the data but not here is still shown, after these,
 * alphabetically, labelled by capitalizing the stored value, until someone
 * adds it to this table.
 *
 * `ventilation` reads "Air Handling and Ventilation": per Jan Whittington,
 * air handling systems, air systems and ventilation are the same thing, and
 * people associate "air handling" with systems that condition and circulate
 * air, so the combined name is clearer than either alone.
 */
export const HVAC_CATEGORIES: readonly HvacCategory[] = [
  { type: 'heating', label: 'Heating', coverageWeight: 0.3 },
  {
    type: 'ventilation',
    label: 'Air Handling and Ventilation',
    coverageWeight: 0.4,
  },
  { type: 'cooling', label: 'Cooling', coverageWeight: 0.3 },
  { type: 'efficiency', label: 'Efficiency', coverageWeight: 0 },
];

/**
 * Category of the final group, holding HVACs with no type at all.
 *
 * Empty string because no real type can produce it: values are trimmed and
 * blanks discarded before grouping. A word such as `'other'` could one day
 * collide with a real hvac_type value.
 */
export const UNTYPED_HVAC_CATEGORY = '';

export interface HvacGroup {
  category: string;
  label: string;
  items: HVAC[];
}

/**
 * The distinct categories of one HVAC row: its `hvacType` values trimmed and
 * lowercased, blanks dropped, duplicates collapsed. The one normalisation shared
 * by grouping and coverage weighting.
 */
function normalizedHvacTypes(hvac: HVAC): Set<string> {
  // `?? []`: an old cached payload may predate the field.
  return new Set(
    (hvac.hvacType ?? [])
      .map((value) => value.trim().toLowerCase())
      .filter((value) => value !== ''),
  );
}

const coverageWeightByType = new Map(
  HVAC_CATEGORIES.map(({ type, coverageWeight }) => [type, coverageWeight]),
);

/**
 * The share of the HVAC system coverage one row contributes at its limit: the
 * sum of the `coverageWeight`s of its distinct categories. A heating+cooling
 * heat pump weighs 0.6; an efficiency-only control 0. Unknown or missing types
 * weigh 0, so an unclassified row cannot inflate coverage. Not capped.
 */
export function getHvacCoverageWeight(hvac: HVAC): number {
  let weight = 0;
  for (const category of normalizedHvacTypes(hvac)) {
    weight += coverageWeightByType.get(category) ?? 0;
  }
  return weight;
}

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Split a list of HVACs into ordered category groups by `hvacType`.
 *
 * Expects the output of `getAvailableHvacList` and does no filtering or
 * sorting of its own: items keep their input order within each group.
 *
 * - Groups follow `HVAC_CATEGORIES`; unlisted types follow, alphabetically.
 * - Values are trimmed and lowercased; duplicates within one row collapse, so
 *   a row appears at most once per group, and once in each of its groups.
 * - Rows with no type (empty, blank or missing `hvacType`) form a final
 *   `UNTYPED_HVAC_CATEGORY` group labelled "Other" — never dropped.
 * - Groups with no items are omitted.
 * - Labels come from `HVAC_CATEGORIES`; an unlisted type is labelled by
 *   capitalizing its stored value.
 */
export function groupHvacsByType(hvacs: HVAC[]): HvacGroup[] {
  const itemsByCategory = new Map<string, HVAC[]>();
  const untyped: HVAC[] = [];

  for (const hvac of hvacs) {
    const categories = normalizedHvacTypes(hvac);
    if (categories.size === 0) {
      untyped.push(hvac);
      continue;
    }
    for (const category of categories) {
      const items = itemsByCategory.get(category) ?? [];
      items.push(hvac);
      itemsByCategory.set(category, items);
    }
  }

  const labelByType = new Map(
    HVAC_CATEGORIES.map(({ type, label }) => [type, label]),
  );
  const known = HVAC_CATEGORIES.map(({ type }) => type);
  const unknown = [...itemsByCategory.keys()]
    .filter((category) => !labelByType.has(category))
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

  const groups: HvacGroup[] = [];
  for (const category of [...known, ...unknown]) {
    const items = itemsByCategory.get(category);
    if (items) {
      const label = labelByType.get(category) ?? capitalize(category);
      groups.push({ category, label, items });
    }
  }

  if (untyped.length > 0) {
    groups.push({
      category: UNTYPED_HVAC_CATEGORY,
      label: 'Other',
      items: untyped,
    });
  }

  return groups;
}

export function computeHVACMinMax(hvacs: HVAC[]): HVACMinMax | undefined {
  if (hvacs.length === 0) return undefined;

  const minMax: HVACMinMax = {
    capitalCostPerUnit: { min: Infinity, max: -Infinity },
    energyUseComparedToConventional: { min: Infinity, max: -Infinity },
    energyUsePerYr: { min: Infinity, max: -Infinity },
    maintenanceCostPerUnit: { min: Infinity, max: -Infinity },
  };

  const setMinMax = (n: { min: number; max: number }, v: number) => {
    n.min = Math.min(n.min, v);
    n.max = Math.max(n.max, v);
  };

  for (const hvac of hvacs) {
    setMinMax(
      minMax.capitalCostPerUnit,
      hvac.capitalCostPerUnit * hvac.unitsNeededPerSqft,
    );
    setMinMax(minMax.maintenanceCostPerUnit, hvac.maintenanceCostPerUnit);
    setMinMax(
      minMax.energyUsePerYr,
      hvac.energyUsePerYr * hvac.unitsNeededPerSqft,
    );
    setMinMax(
      minMax.energyUseComparedToConventional,
      hvac.energyUseComparedToConventional,
    );
  }

  return minMax;
}
